import { icon } from '../icons.js';

function element(tagName, options = {}, children = []) {
  const node = document.createElement(tagName);
  if (options.className) node.className = options.className;
  if (options.text !== undefined) node.textContent = options.text;
  for (const [name, value] of Object.entries(options.attributes || {})) {
    if (value !== null && value !== undefined) node.setAttribute(name, String(value));
  }
  for (const child of children) {
    if (child) node.append(child);
  }
  return node;
}

function controlId(questionId, suffix = '') {
  return `${questionId.replace(/[^a-z0-9]+/giu, '-')}${suffix ? `-${suffix}` : ''}`;
}

function cloneObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? { ...value } : {};
}

function emit(onChange, questionId, value) {
  onChange({ questionId, value });
}

function errorMessage(question, error) {
  if (!error) return null;
  return element('p', {
    className: 'assessment-error',
    text: error,
    attributes: { id: `${controlId(question.id)}-error`, role: 'alert', tabindex: '-1' },
  });
}

export function clearQuestionError(root, questionId) {
  const base = controlId(questionId);
  root.querySelector(`#${base}-error`)?.remove();
  const field = root.querySelector(`#${base}`);
  if (!field) return;
  field.removeAttribute('aria-invalid');
  const describedBy = field.getAttribute('aria-describedby');
  if (!describedBy) return;
  const remaining = describedBy.split(/\s+/u).filter((id) => id && id !== `${base}-error`).join(' ');
  if (remaining) field.setAttribute('aria-describedby', remaining);
  else field.removeAttribute('aria-describedby');
}

function describedBy(question, error, extraId = '') {
  return [extraId, error ? `${controlId(question.id)}-error` : ''].filter(Boolean).join(' ') || null;
}

function resizeTextarea(textarea) {
  textarea.style.height = 'auto';
  textarea.style.height = `${Math.max(textarea.scrollHeight, 132)}px`;
}

function counterFor(textarea, { maxLength, maxWords, id } = {}) {
  const count = element('span', {
    className: 'character-count',
    attributes: id ? { id } : {},
  });
  const update = () => {
    const trimmed = textarea.value.trim();
    const words = trimmed ? trimmed.split(/\s+/u).length : 0;
    const over = maxWords ? words > maxWords : textarea.value.length > maxLength;
    count.textContent = maxWords
      ? `${words} / ${maxWords} words`
      : `${textarea.value.length} / ${maxLength}`;
    count.classList.toggle('is-over', over);
  };
  update();
  return { count, update };
}

function textareaControl(question, value, onChange, error) {
  const id = controlId(question.id);
  const countId = `${id}-count`;
  const textarea = element('textarea', {
    className: 'assessment-textarea',
    attributes: {
      id,
      name: question.id,
      rows: '5',
      maxlength: question.maxLength,
      'aria-describedby': describedBy(question, error, countId),
      'aria-invalid': error ? 'true' : null,
    },
  });
  textarea.value = typeof value === 'string' ? value : '';

  const { count, update: updateCount } = counterFor(textarea, {
    maxLength: question.maxLength,
    maxWords: question.maxWords,
    id: countId,
  });
  textarea.addEventListener('input', () => {
    resizeTextarea(textarea);
    updateCount();
    emit(onChange, question.id, textarea.value);
  });
  requestAnimationFrame(() => resizeTextarea(textarea));

  return element('div', { className: 'question-block' }, [
    element('label', {
      className: 'question-label',
      text: question.label || question.prompt,
      attributes: { for: id },
    }),
    question.label ? element('p', { className: 'question-prompt', text: question.prompt }) : null,
    textarea,
    element('div', { className: 'field-meta' }, [
      element('span', {
        text: question.required
          ? `Minimum ${question.minLength} characters`
          : question.requiredWhenCommitmentHasConstraint
            ? 'Required only if you noted a constraint or a "No" above'
            : 'Optional',
      }),
      count,
    ]),
    errorMessage(question, error),
  ]);
}

function optionCard({ question, option, type, checked, onInput, error, name = question.id }) {
  const id = controlId(name, option.id);
  const input = element('input', {
    className: 'option-card__input',
    attributes: {
      id,
      name,
      type,
      value: option.id,
      checked: checked ? '' : null,
      'aria-describedby': describedBy(question, error),
    },
  });
  input.checked = checked;
  input.addEventListener('change', onInput);
  return element('label', { className: 'option-card', attributes: { for: id } }, [
    input,
    element('span', { className: 'option-card__marker', attributes: { 'aria-hidden': 'true' } }),
    element('span', { className: 'option-card__copy' }, [
      element('strong', { text: option.label }),
      option.description ? element('small', { text: option.description }) : null,
    ]),
  ]);
}

function choiceControl(question, value, onChange, error, { multiple = false } = {}) {
  let selected = multiple && Array.isArray(value) ? [...value] : [];
  const inputsByOptionId = new Map();
  const cards = question.options.map((option) => optionCard({
    question,
    option,
    type: multiple ? 'checkbox' : 'radio',
    checked: multiple ? selected.includes(option.id) : value === option.id,
    error,
    onInput(event) {
      if (!multiple) {
        emit(onChange, question.id, option.id);
        return;
      }
      let next = [...selected];
      if (event.currentTarget.checked) next = [...next, option.id];
      else next = next.filter((id) => id !== option.id);

      if (question.exclusiveOptionId) {
        next = option.id === question.exclusiveOptionId && event.currentTarget.checked
          ? [option.id]
          : next.filter((id) => id !== question.exclusiveOptionId);
        for (const [id, input] of inputsByOptionId) {
          input.checked = next.includes(id);
        }
      }
      if (question.maxItems && next.length > question.maxItems) {
        event.currentTarget.checked = false;
        return;
      }
      selected = next;
      emit(onChange, question.id, next);
    },
  }));
  cards.forEach((card, index) => {
    inputsByOptionId.set(question.options[index].id, card.querySelector('input'));
  });

  return element('fieldset', { className: 'question-block question-fieldset' }, [
    element('legend', { className: 'question-label', text: question.prompt }),
    question.scenario ? element('div', { className: 'scenario-copy', text: question.scenario }) : null,
    element('div', { className: 'option-list' }, cards),
    errorMessage(question, error),
  ]);
}

function acknowledgementControl(question, value, onChange, error) {
  const option = { id: 'acknowledged', label: question.prompt };
  return element('fieldset', { className: 'question-block question-fieldset' }, [
    element('legend', { className: 'sr-only', text: 'Ready to begin' }),
    optionCard({
      question,
      option,
      type: 'checkbox',
      checked: value === true,
      error,
      onInput(event) { emit(onChange, question.id, event.currentTarget.checked); },
    }),
    errorMessage(question, error),
  ]);
}

function capabilityGridControl(question, value, onChange, error) {
  let current = cloneObject(value);
  const rows = question.capabilities.map((capability) => {
    const answer = cloneObject(current[capability.id]);
    const groupName = `${question.id}.${capability.id}`;
    const evidenceId = controlId(groupName, 'evidence');
    const evidence = element('textarea', {
      className: 'assessment-textarea assessment-textarea--compact',
      attributes: {
        id: evidenceId,
        name: `${groupName}.evidence`,
        rows: '3',
        maxlength: '1800',
        placeholder: 'Add the concrete beyond-one-venue example.',
      },
    });
    evidence.value = typeof answer.evidence === 'string' ? answer.evidence : '';
    const { count: evidenceCount, update: updateEvidenceCount } = counterFor(evidence, { maxLength: 1800 });
    const evidenceWrap = element('div', {
      className: 'capability-evidence',
      attributes: { hidden: answer.rating === question.highestRatingId ? null : '' },
    }, [
      element('label', { text: 'Evidence for this claim', attributes: { for: evidenceId } }),
      evidence,
      element('div', { className: 'field-meta' }, [
        element('span', { text: 'Required' }),
        evidenceCount,
      ]),
    ]);
    evidenceWrap.hidden = answer.rating !== question.highestRatingId;
    evidence.addEventListener('input', () => {
      updateEvidenceCount();
      const next = cloneObject(current);
      next[capability.id] = { ...cloneObject(next[capability.id]), evidence: evidence.value };
      current = next;
      emit(onChange, question.id, next);
    });

    const options = question.options.map((option) => optionCard({
      question,
      option,
      type: 'radio',
      name: groupName,
      checked: answer.rating === option.id,
      error,
      onInput() {
        const next = cloneObject(current);
        const previous = cloneObject(next[capability.id]);
        next[capability.id] = option.id === question.highestRatingId
          ? { rating: option.id, evidence: previous.evidence || '' }
          : { rating: option.id };
        current = next;
        evidenceWrap.hidden = option.id !== question.highestRatingId;
        emit(onChange, question.id, next);
      },
    }));

    return element('fieldset', { className: 'capability-row' }, [
      element('legend', { className: 'capability-row__title', text: capability.label }),
      element('p', { className: 'capability-row__statement', text: capability.statement }),
      element('div', { className: 'capability-options' }, options),
      evidenceWrap,
    ]);
  });

  return element('fieldset', { className: 'question-block question-fieldset' }, [
    element('legend', { className: 'question-label', text: question.prompt }),
    element('div', { className: 'anchor-guide' }, question.options.map((option, index) =>
      element('div', {}, [
        element('b', { text: `${index + 1}. ${option.label}` }),
        element('span', { text: option.description }),
      ]))),
    element('div', { className: 'capability-grid' }, rows),
    errorMessage(question, error),
  ]);
}

function evidencePairControl(question, value, onChange, error) {
  const items = Array.from({ length: question.itemCount }, (_, index) => ({
    capabilityId: value?.[index]?.capabilityId || '',
    response: value?.[index]?.response || '',
  }));
  const errorExampleIndex = Number(error?.match(/Example (\d+)/u)?.[1] || 0) - 1;
  const capabilityError = /capability|Complete Example/u.test(error || '');
  const responseError = /evidence|detail|Shorten|Complete Example/u.test(error || '');
  const targetedError = errorExampleIndex >= 0 && (capabilityError || responseError);
  const questionError = targetedError ? null : errorMessage(question, error);
  const invalidFields = [];
  const validationMessages = [];
  const clearValidationState = () => {
    questionError?.remove();
    validationMessages.forEach((message) => message.remove());
    invalidFields.forEach(({ field, describedById }) => {
      field.removeAttribute('aria-invalid');
      if (describedById) field.setAttribute('aria-describedby', describedById);
      else field.removeAttribute('aria-describedby');
    });
  };
  const update = (index, field, nextValue) => {
    clearValidationState();
    items[index][field] = nextValue;
    const next = items.map((item) => ({ ...item }));
    emit(onChange, question.id, next);
  };

  const itemControls = items.map((item, index) => {
    const selectId = controlId(question.id, `capability-${index}`);
    const responseId = controlId(question.id, `response-${index}`);
    const errorId = controlId(question.id, `error-${index}`);
    const countId = controlId(question.id, `response-${index}-count`);
    const validCapability = question.capabilities.some(({ id }) => id === item.capabilityId);
    const duplicateCapability = validCapability && items.findIndex(
      ({ capabilityId }) => capabilityId === item.capabilityId,
    ) !== index;
    const responseLength = typeof item.response === 'string' ? item.response.trim().length : 0;
    const capabilityInvalid = index === errorExampleIndex && capabilityError && (
      !validCapability || duplicateCapability
    );
    const responseInvalid = !capabilityInvalid && index === errorExampleIndex && responseError && (
      responseLength < question.minLength || responseLength > question.maxLength
    );
    const localError = capabilityInvalid || responseInvalid
      ? element('p', {
        className: 'assessment-error assessment-error--field',
        text: error,
        attributes: { id: errorId, role: 'alert' },
      })
      : null;
    if (localError) validationMessages.push(localError);
    const select = element('select', {
      className: 'assessment-select',
      attributes: {
        id: selectId,
        name: `${question.id}.${index}.capabilityId`,
        'aria-invalid': capabilityInvalid ? 'true' : null,
        'aria-describedby': capabilityInvalid ? errorId : null,
      },
    }, [
      element('option', { text: 'Choose a capability', attributes: { value: '' } }),
      ...question.capabilities.map((capability) => element('option', {
        text: capability.label,
        attributes: { value: capability.id },
      })),
    ]);
    select.value = item.capabilityId;
    select.addEventListener('change', () => update(index, 'capabilityId', select.value));

    const textarea = element('textarea', {
      className: 'assessment-textarea',
      attributes: {
        id: responseId,
        name: `${question.id}.${index}.response`,
        rows: '5',
        maxlength: question.maxLength,
        'aria-invalid': responseInvalid ? 'true' : null,
        'aria-describedby': [countId, responseInvalid ? errorId : ''].filter(Boolean).join(' '),
      },
    });
    if (capabilityInvalid) invalidFields.push({ field: select, describedById: '' });
    if (responseInvalid) invalidFields.push({ field: textarea, describedById: countId });
    textarea.value = item.response;
    const { count, update: updateCount } = counterFor(textarea, {
      maxLength: question.maxLength,
      id: countId,
    });
    textarea.addEventListener('input', () => {
      resizeTextarea(textarea);
      updateCount();
      update(index, 'response', textarea.value);
    });
    requestAnimationFrame(() => resizeTextarea(textarea));

    return element('div', { className: 'evidence-item' }, [
      element('span', { className: 'item-number', text: String(index + 1).padStart(2, '0') }),
      element('div', { className: 'evidence-item__fields' }, [
        element('label', { className: 'field-label', text: `Capability for Example ${index + 1}`, attributes: { for: selectId } }),
        select,
        capabilityInvalid ? localError : null,
        element('label', { className: 'field-label', text: `Your evidence for Example ${index + 1}`, attributes: { for: responseId } }),
        textarea,
        element('div', { className: 'field-meta evidence-field-meta' }, [
          element('span', { text: `Minimum ${question.minLength} characters` }),
          count,
        ]),
        responseInvalid ? localError : null,
      ]),
    ]);
  });

  return element('fieldset', { className: 'question-block question-fieldset' }, [
    element('legend', { className: 'question-label', text: question.prompt }),
    element('div', { className: 'evidence-pair' }, itemControls),
    questionError,
  ]);
}

function commitmentGridControl(question, value, onChange, error) {
  let current = cloneObject(value);
  const rows = question.commitments.map((commitment, index) => {
    const answer = cloneObject(current[commitment.id]);
    const name = `${question.id}.${commitment.id}`;
    const explanationId = controlId(name, 'explanation');
    const explanation = element('textarea', {
      className: 'assessment-textarea assessment-textarea--compact',
      attributes: {
        id: explanationId,
        name: `${name}.explanation`,
        rows: '3',
        maxlength: question.explanationMaxLength,
        placeholder: 'Explain the practical constraint clearly.',
      },
    });
    explanation.value = answer.explanation || '';
    const { count: explanationCount, update: updateExplanationCount } = counterFor(explanation, {
      maxLength: question.explanationMaxLength,
    });
    const explanationWrap = element('div', {
      className: 'commitment-explanation',
    }, [
      element('label', { text: 'What should we understand?', attributes: { for: explanationId } }),
      explanation,
      element('div', { className: 'field-meta' }, [
        element('span', { text: `Minimum ${question.explanationMinLength} characters` }),
        explanationCount,
      ]),
    ]);
    explanationWrap.hidden = !answer.choice || answer.choice === 'yes';
    explanation.addEventListener('input', () => {
      updateExplanationCount();
      const next = cloneObject(current);
      next[commitment.id] = { ...cloneObject(next[commitment.id]), explanation: explanation.value };
      current = next;
      emit(onChange, question.id, next);
    });

    const cards = question.options.map((option) => optionCard({
      question,
      option,
      type: 'radio',
      name,
      checked: answer.choice === option.id,
      error,
      onInput() {
        const next = cloneObject(current);
        next[commitment.id] = option.id === 'yes'
          ? { choice: option.id }
          : { choice: option.id, explanation: cloneObject(next[commitment.id]).explanation || '' };
        current = next;
        explanationWrap.hidden = option.id === 'yes';
        emit(onChange, question.id, next);
      },
    }));

    return element('fieldset', { className: 'commitment-row' }, [
      element('legend', { className: 'commitment-row__title' }, [
        element('span', { text: String(index + 1).padStart(2, '0') }),
        element('strong', { text: commitment.statement }),
      ]),
      element('div', { className: 'commitment-options' }, cards),
      explanationWrap,
    ]);
  });

  return element('fieldset', { className: 'question-block question-fieldset' }, [
    element('legend', { className: 'question-label', text: question.prompt }),
    element('div', { className: 'commitment-grid' }, rows),
    errorMessage(question, error),
  ]);
}

function rankControl(question, value, onChange, error) {
  const selected = Array.from({ length: question.rankCount }, (_, index) => (
    Array.isArray(value) && typeof value[index] === 'string' ? value[index] : ''
  ));
  const pickers = [];

  const closePicker = (picker, focusTrigger = false) => {
    picker.menu.hidden = true;
    picker.trigger.setAttribute('aria-expanded', 'false');
    if (focusTrigger) picker.trigger.focus();
  };

  const updatePickers = () => {
    pickers.forEach((picker, index) => {
      const chosen = question.options.find(({ id }) => id === selected[index]);
      const displayText = chosen?.label || 'Choose a move';
      const waitingForPrevious = index > 0 && !selected[index - 1];
      picker.triggerText.textContent = displayText;
      picker.trigger.disabled = waitingForPrevious;
      picker.trigger.setAttribute(
        'aria-label',
        waitingForPrevious
          ? `Priority ${index + 1}: Complete Priority ${index} first`
          : `Priority ${index + 1}: ${displayText}`,
      );
      picker.trigger.title = waitingForPrevious ? `Complete Priority ${index} first` : '';

      picker.optionButtons.forEach(({ button, option }) => {
        const selectedHere = selected[index] === option.id;
        const selectedElsewhere = selected.some((id, selectedIndex) => (
          selectedIndex !== index && id === option.id
        ));
        button.disabled = selectedElsewhere;
        button.setAttribute('aria-pressed', String(selectedHere));
        button.title = selectedElsewhere ? 'Already used in another priority' : '';
      });
    });
  };

  const rows = Array.from({ length: question.rankCount }, (_, index) => {
    const id = controlId(question.id, `rank-${index + 1}`);
    const labelId = `${id}-label`;
    const menuId = `${id}-options`;
    const triggerText = element('span', { className: 'rank-picker__trigger-copy' });
    const trigger = element('button', {
      className: 'rank-picker__trigger',
      attributes: {
        type: 'button',
        'aria-controls': menuId,
        'aria-expanded': 'false',
      },
    }, [triggerText, icon('expand', 'rank-picker__chevron')]);
    const optionButtons = question.options.map((option) => {
      const button = element('button', {
        className: 'rank-picker__option',
        attributes: { type: 'button', 'aria-pressed': 'false' },
      }, [
        icon('check', 'rank-picker__option-icon'),
        element('span', { text: option.label }),
      ]);
      return { button, option };
    });
    const menu = element('div', {
      className: 'rank-picker__menu',
      attributes: { id: menuId, hidden: '', role: 'group', 'aria-labelledby': labelId },
    }, optionButtons.map(({ button }) => button));
    const pickerElement = element('div', { className: 'rank-picker' }, [trigger, menu]);
    const picker = { menu, optionButtons, trigger, triggerText };
    pickers.push(picker);

    trigger.addEventListener('click', () => {
      const shouldOpen = menu.hidden;
      pickers.forEach((item) => closePicker(item));
      menu.hidden = !shouldOpen;
      trigger.setAttribute('aria-expanded', String(shouldOpen));
      if (shouldOpen) {
        const desiredMenuHeight = 260;
        const viewportGap = 22;
        let triggerBounds = trigger.getBoundingClientRect();
        let availableHeight = window.innerHeight - triggerBounds.bottom - viewportGap;
        if (availableHeight < desiredMenuHeight) {
          window.scrollBy({ top: desiredMenuHeight - availableHeight, behavior: 'auto' });
          triggerBounds = trigger.getBoundingClientRect();
          availableHeight = window.innerHeight - triggerBounds.bottom - viewportGap;
        }
        menu.style.maxHeight = `${Math.max(120, Math.min(460, availableHeight))}px`;
      }
    });
    optionButtons.forEach(({ button, option }) => {
      button.addEventListener('click', () => {
        selected[index] = option.id;
        closePicker(picker, true);
        updatePickers();
        emit(onChange, question.id, selected.filter(Boolean));
      });
    });
    pickerElement.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || menu.hidden) return;
      event.preventDefault();
      closePicker(picker, true);
    });
    pickerElement.addEventListener('focusout', (event) => {
      if (!pickerElement.contains(event.relatedTarget)) closePicker(picker);
    });

    return element('div', { className: 'rank-row' }, [
      element('span', {
        className: 'rank-row__label',
        text: `Priority ${index + 1}`,
        attributes: { id: labelId },
      }),
      pickerElement,
    ]);
  });
  updatePickers();

  return element('fieldset', { className: 'question-block question-fieldset' }, [
    element('legend', { className: 'question-label', text: question.prompt }),
    element('div', { className: 'scenario-copy', text: question.scenario }),
    element('div', { className: 'rank-list' }, rows),
    errorMessage(question, error),
  ]);
}

function dateControl(question, value, onChange, error) {
  const id = controlId(question.id);
  const input = element('input', {
    className: 'assessment-input assessment-input--date',
    attributes: {
      id,
      name: question.id,
      type: 'date',
      value: value || question.defaultValue || '',
      'aria-invalid': error ? 'true' : null,
    },
  });
  input.value = value || question.defaultValue || '';
  input.addEventListener('change', () => emit(onChange, question.id, input.value));
  return element('div', { className: 'question-block' }, [
    element('label', { className: 'question-label', text: question.prompt, attributes: { for: id } }),
    input,
    errorMessage(question, error),
  ]);
}

function dateRangesControl(question, value, onChange, error) {
  let current = value && typeof value === 'object'
    ? { selection: value.selection, ranges: (value.ranges || []).map((range) => ({ ...range })) }
    : { selection: '', ranges: [] };
  const body = element('div', { className: 'repeatable-list' });
  const add = element('button', {
    className: 'text-button',
    attributes: { type: 'button' },
  }, [element('span', { text: 'Add another date range' })]);
  const action = element('div', { className: 'repeatable-action' }, [add]);
  const renderRows = () => {
    body.replaceChildren();
    action.hidden = current.selection !== 'planned';
    if (current.selection !== 'planned') return;
    current.ranges.forEach((range, index) => {
      const row = element('div', { className: 'repeatable-row repeatable-row--dates' });
      for (const [field, label, type] of [
        ['startDate', 'From', 'date'],
        ['endDate', 'To', 'date'],
        ['details', 'Short details', 'text'],
      ]) {
        const id = controlId(question.id, `${field}-${index}`);
        const input = element('input', {
          className: 'assessment-input',
          attributes: {
            id,
            type,
            value: range[field] || '',
            max: field === 'endDate' ? question.latestDate : null,
          },
        });
        input.value = range[field] || '';
        input.addEventListener('input', () => {
          current.ranges[index][field] = input.value;
          emit(onChange, question.id, current);
        });
        row.append(element('label', { text: label, attributes: { for: id } }), input);
      }
      const remove = element('button', {
        className: 'icon-button icon-button--danger',
        attributes: { type: 'button', 'aria-label': `Remove leave range ${index + 1}`, title: 'Remove leave range' },
      }, [element('span', { text: 'Remove' })]);
      remove.addEventListener('click', () => {
        current.ranges.splice(index, 1);
        emit(onChange, question.id, current);
        renderRows();
      });
      row.append(remove);
      body.append(row);
    });
  };

  const options = question.selectionOptions.map((option) => optionCard({
    question,
    option,
    type: 'radio',
    checked: current.selection === option.id,
    error,
    onInput() {
      current = {
        selection: option.id,
        ranges: option.id === 'planned'
          ? (current.ranges.length ? current.ranges : [{ startDate: '', endDate: '', details: '' }])
          : [],
      };
      emit(onChange, question.id, current);
      renderRows();
    },
  }));
  add.addEventListener('click', () => {
    if (current.selection !== 'planned') return;
    current.ranges.push({ startDate: '', endDate: '', details: '' });
    emit(onChange, question.id, current);
    renderRows();
  });
  renderRows();

  return element('fieldset', { className: 'question-block question-fieldset' }, [
    element('legend', { className: 'question-label', text: question.prompt }),
    element('div', { className: 'option-list option-list--inline' }, options),
    body,
    action,
    errorMessage(question, error),
  ]);
}

function interviewWindowsControl(question, value, onChange, error) {
  let current = Array.isArray(value) && value.length
    ? value.map((window) => ({ ...window }))
    : Array.from({ length: question.minItems }, () => ({ date: '', startTime: '', endTime: '' }));
  const body = element('div', { className: 'repeatable-list' });
  const commit = () => emit(onChange, question.id, current.map((window) => ({ ...window })));
  const renderRows = () => {
    body.replaceChildren();
    current.forEach((window, index) => {
      const row = element('div', { className: 'repeatable-row repeatable-row--windows' });
      for (const [field, label, type] of [
        ['date', 'Date', 'date'],
        ['startTime', 'Start', 'time'],
        ['endTime', 'End', 'time'],
      ]) {
        const id = controlId(question.id, `${field}-${index}`);
        const input = element('input', {
          className: 'assessment-input',
          attributes: { id, type, value: window[field] || '' },
        });
        input.value = window[field] || '';
        input.addEventListener('input', () => {
          current[index][field] = input.value;
          commit();
        });
        row.append(element('label', { text: label, attributes: { for: id } }), input);
      }
      if (current.length > question.minItems) {
        const remove = element('button', {
          className: 'icon-button icon-button--danger',
          attributes: { type: 'button', 'aria-label': `Remove interview window ${index + 1}`, title: 'Remove interview window' },
        }, [element('span', { text: 'Remove' })]);
        remove.addEventListener('click', () => {
          current.splice(index, 1);
          commit();
          renderRows();
        });
        row.append(remove);
      }
      body.append(row);
    });
  };
  const add = element('button', {
    className: 'text-button',
    attributes: { type: 'button' },
  }, [element('span', { text: 'Add another interview window' })]);
  add.addEventListener('click', () => {
    current.push({ date: '', startTime: '', endTime: '' });
    commit();
    renderRows();
  });
  renderRows();

  return element('fieldset', { className: 'question-block question-fieldset' }, [
    element('legend', { className: 'question-label', text: question.prompt }),
    element('p', { className: 'question-prompt', text: `Add at least ${question.minItems} windows of ${question.minMinutes}-${question.maxMinutes} minutes.` }),
    body,
    element('div', { className: 'repeatable-action' }, [add]),
    errorMessage(question, error),
  ]);
}

function declarationsControl(question, value, onChange, error) {
  let current = cloneObject(value);
  const options = question.declarations.map((declaration) => ({
    id: declaration.id,
    label: declaration.statement,
  }));
  const cards = options.map((option) => optionCard({
    question,
    option,
    type: 'checkbox',
    checked: current[option.id] === true,
    error,
    name: `${question.id}.${option.id}`,
    onInput(event) {
      current = { ...current, [option.id]: event.currentTarget.checked };
      emit(onChange, question.id, current);
    },
  }));
  return element('fieldset', { className: 'question-block question-fieldset' }, [
    element('legend', { className: 'question-label', text: question.prompt }),
    element('div', { className: 'option-list' }, cards),
    errorMessage(question, error),
  ]);
}

export function renderQuestionControl({ question, value, onChange, error = '' }) {
  switch (question.type) {
    case 'acknowledgement':
      return acknowledgementControl(question, value, onChange, error);
    case 'capability-grid':
      return capabilityGridControl(question, value, onChange, error);
    case 'evidence-pair':
      return evidencePairControl(question, value, onChange, error);
    case 'commitment-grid':
      return commitmentGridControl(question, value, onChange, error);
    case 'single-choice':
      return choiceControl(question, value, onChange, error);
    case 'multi-choice':
    case 'support-priorities':
      return choiceControl(question, value, onChange, error, { multiple: true });
    case 'rank':
      return rankControl(question, value, onChange, error);
    case 'text':
      return textareaControl(question, value, onChange, error);
    case 'date':
      return dateControl(question, value, onChange, error);
    case 'date-ranges':
      return dateRangesControl(question, value, onChange, error);
    case 'interview-windows':
      return interviewWindowsControl(question, value, onChange, error);
    case 'declarations':
      return declarationsControl(question, value, onChange, error);
    default:
      return element('p', { className: 'assessment-error', text: 'This question is unavailable.' });
  }
}
